/**
 * PQL parser — recursive descent over the tokenizer, producing a PqlQuery
 * whose leaves are ordinary FilterRule objects (evaluable by the existing
 * item-list engine, representable in the filter builder when two-level).
 *
 * Everything the statement language supports desugars to the six leaf
 * operators ('in' | 'eq' | 'boolean' | 'text' | 'range' | 'date-range') plus
 * 'empty', with NOT nodes providing negation:
 *
 *   field != v            → NOT (field IN (v))
 *   field NOT IN (a, b)   → NOT (field IN (a, b))
 *   field !~ "s"          → NOT (field ~ "s")
 *   altitude > 3          → range/not desugar (numeric), or IN of the
 *                           numeric option values above 3 (numeric set field)
 *   created >= -30d       → date-range with the duration resolved via
 *                           catalog.context.now (statement text stays relative;
 *                           the AST holds the resolved date)
 *   field IS NOT EMPTY    → NOT (field IS EMPTY)
 */

import { tokenize, type Token } from './tokenizer.ts';
import {
  PqlError,
  type FilterFieldOption,
  type FilterRule,
  type ParseResult,
  type PqlCatalog,
  type PqlFieldSpec,
  type PqlQuery,
  type QueryNode,
  type SortSpec,
} from './types.ts';

const KEYWORDS = new Set(['and', 'or', 'not', 'in', 'is', 'empty', 'order', 'by', 'asc', 'desc']);

interface RawValue {
  token: Token;
  kind: 'string' | 'bareword' | 'number' | 'date' | 'duration' | 'function' | 'numrange' | 'daterange';
  /** Unquoted text (function name for kind 'function'; lower bound for range kinds). */
  text: string;
  /** Upper bound for 'numrange' / 'daterange'. */
  text2?: string;
}

export function parse(input: string, catalog: PqlCatalog): PqlQuery {
  return new Parser(tokenize(input), catalog).parseQuery();
}

export function safeParse(input: string, catalog: PqlCatalog): ParseResult {
  try {
    return { ok: true, query: parse(input, catalog) };
  } catch (e) {
    if (e instanceof PqlError) return { ok: false, error: e };
    throw e;
  }
}

class Parser {
  private pos = 0;
  private ruleSeq = 0;
  private readonly tokens: Token[];
  private readonly catalog: PqlCatalog;

  constructor(tokens: Token[], catalog: PqlCatalog) {
    this.tokens = tokens;
    this.catalog = catalog;
  }

  // ── token helpers ─────────────────────────────────────────────────────────

  private peek(offset = 0): Token {
    return this.tokens[Math.min(this.pos + offset, this.tokens.length - 1)];
  }

  private next(): Token {
    const t = this.tokens[this.pos];
    if (t.type !== 'eof') this.pos++;
    return t;
  }

  private isKeyword(t: Token, word: string): boolean {
    return t.type === 'ident' && t.text.toLowerCase() === word;
  }

  private matchKeyword(word: string): boolean {
    if (this.isKeyword(this.peek(), word)) {
      this.next();
      return true;
    }
    return false;
  }

  private fail(message: string, t: Token, suggestion?: string): never {
    throw new PqlError(message, t.start, t.end, suggestion);
  }

  // ── entry ─────────────────────────────────────────────────────────────────

  parseQuery(): PqlQuery {
    let where: QueryNode | null = null;
    if (this.peek().type !== 'eof' && !this.isKeyword(this.peek(), 'order')) {
      where = this.parseExpr();
    }
    const orderBy = this.parseOrderBy();
    const trailing = this.peek();
    if (trailing.type !== 'eof') {
      this.fail(`Unexpected "${trailing.text}" — expected AND, OR, ORDER BY, or end of query`, trailing);
    }
    return { where, orderBy };
  }

  private parseOrderBy(): SortSpec[] {
    if (!this.matchKeyword('order')) return [];
    if (!this.matchKeyword('by')) this.fail('Expected BY after ORDER', this.peek());
    const specs: SortSpec[] = [];
    do {
      const t = this.peek();
      if (t.type !== 'ident') this.fail('Expected a field name to sort by', t);
      this.next();
      // Resolve to the canonical key when known; sort is tolerant of
      // registry fields that aren't filterable (e.g. title), so unknown
      // names pass through raw rather than erroring.
      const spec = this.catalog.resolveField(t.text);
      let direction: 'asc' | 'desc' = 'asc';
      if (this.matchKeyword('desc')) direction = 'desc';
      else if (this.matchKeyword('asc')) direction = 'asc';
      specs.push({ field: spec?.key ?? t.text, direction });
    } while (this.peek().type === 'comma' && (this.next(), true));
    return specs;
  }

  // ── boolean expression grammar (OR < AND < NOT < parens/clause) ──────────

  private parseExpr(): QueryNode {
    const children = [this.parseTerm()];
    while (this.matchKeyword('or')) children.push(this.parseTerm());
    return this.combine('or', children);
  }

  private parseTerm(): QueryNode {
    const children = [this.parseFactor()];
    while (this.matchKeyword('and')) children.push(this.parseFactor());
    return this.combine('and', children);
  }

  /** n-ary flatten: a OR (b OR c) ≡ a OR b OR c — keeps the canonical form stable. */
  private combine(op: 'and' | 'or', children: QueryNode[]): QueryNode {
    if (children.length === 1) return children[0];
    const flat: QueryNode[] = [];
    for (const c of children) {
      if ('op' in c && c.op === op) flat.push(...c.children);
      else flat.push(c);
    }
    return { op, children: flat };
  }

  private parseFactor(): QueryNode {
    if (this.matchKeyword('not')) {
      return { op: 'not', child: this.parseFactor() };
    }
    if (this.peek().type === 'lparen') {
      this.next();
      const inner = this.parseExpr();
      if (this.peek().type !== 'rparen') this.fail('Expected ")"', this.peek());
      this.next();
      return inner;
    }
    return this.parseClause();
  }

  // ── clauses ───────────────────────────────────────────────────────────────

  private parseClause(): QueryNode {
    const fieldTok = this.peek();
    if (fieldTok.type !== 'ident') {
      this.fail(`Expected a field name, found "${fieldTok.text || 'end of query'}"`, fieldTok);
    }
    if (KEYWORDS.has(fieldTok.text.toLowerCase())) {
      this.fail(`Expected a field name, found "${fieldTok.text}"`, fieldTok);
    }
    this.next();
    const spec = this.catalog.resolveField(fieldTok.text);
    if (!spec) {
      const suggestion = this.catalog.suggestField(fieldTok.text);
      this.fail(
        `Unknown field "${fieldTok.text}"${suggestion ? ` — did you mean ${suggestion}?` : ''}`,
        fieldTok,
        suggestion,
      );
    }

    // field IS [NOT] EMPTY
    if (this.matchKeyword('is')) {
      const negated = this.matchKeyword('not');
      if (!this.matchKeyword('empty')) this.fail('Expected EMPTY after IS', this.peek());
      const rule = this.makeRule(spec, 'empty', true, 'is empty');
      return negated ? { op: 'not', child: rule } : rule;
    }

    // field [NOT] IN (a, b, ...)
    const negatedIn = this.isKeyword(this.peek(), 'not') && this.isKeyword(this.peek(1), 'in');
    if (negatedIn) this.next();
    if (this.matchKeyword('in')) {
      if (this.peek().type !== 'lparen') this.fail('Expected "(" after IN', this.peek());
      this.next();
      const values: RawValue[] = [];
      do {
        values.push(this.parseValue());
      } while (this.peek().type === 'comma' && (this.next(), true));
      if (this.peek().type !== 'rparen') this.fail('Expected ")" to close the IN list', this.peek());
      this.next();
      const rule = this.setRuleFromValues(spec, fieldTok, values);
      return negatedIn ? { op: 'not', child: rule } : rule;
    }

    const opTok = this.peek();
    if (opTok.type !== 'op') {
      this.fail(`Expected an operator after "${fieldTok.text}" (=, !=, ~, >, IN, IS EMPTY, …)`, opTok);
    }
    this.next();
    const value = this.parseValue();
    return this.buildComparison(spec, fieldTok, opTok.text, value);
  }

  private parseValue(): RawValue {
    const t = this.peek();
    switch (t.type) {
      case 'string':
        this.next();
        return { token: t, kind: 'string', text: t.value };
      case 'number':
        this.next();
        return this.maybeRange({ token: t, kind: 'number', text: t.text });
      case 'date':
        this.next();
        return this.maybeRange({ token: t, kind: 'date', text: t.text });
      case 'duration':
        this.next();
        return { token: t, kind: 'duration', text: t.text };
      case 'ident': {
        if (KEYWORDS.has(t.text.toLowerCase())) {
          this.fail(`Expected a value, found "${t.text}"`, t);
        }
        this.next();
        if (this.peek().type === 'lparen') {
          this.next();
          if (this.peek().type !== 'rparen') {
            this.fail(`Expected ")" — ${t.text}() takes no arguments`, this.peek());
          }
          this.next();
          return { token: t, kind: 'function', text: t.text };
        }
        return { token: t, kind: 'bareword', text: t.text };
      }
      default:
        this.fail(`Expected a value, found "${t.text || 'end of query'}"`, t);
    }
  }

  /** `2..5` / `2026-01-01..2026-03-31` bounded-range literals. */
  private maybeRange(first: RawValue): RawValue {
    if (this.peek().type !== 'dotdot') return first;
    this.next();
    const second = this.peek();
    if (second.type !== first.token.type) {
      this.fail(
        `Expected a ${first.kind === 'number' ? 'number' : 'date'} after ".."`,
        second,
      );
    }
    this.next();
    return {
      token: first.token,
      kind: first.kind === 'number' ? 'numrange' : 'daterange',
      text: first.text,
      text2: second.text,
    };
  }

  // ── rule construction (type-directed desugaring) ──────────────────────────

  private makeRule(
    spec: PqlFieldSpec,
    operator: FilterRule['operator'],
    value: FilterRule['value'],
    displayValue: string,
  ): FilterRule {
    return {
      id: `pql-${++this.ruleSeq}`,
      field: spec.key,
      label: spec.label,
      operator,
      value,
      displayValue,
    };
  }

  private buildComparison(spec: PqlFieldSpec, fieldTok: Token, op: string, value: RawValue): QueryNode {
    switch (spec.type) {
      case 'boolean':
        return this.booleanRule(spec, op, value);
      case 'text':
        return this.textRule(spec, op, value);
      case 'range':
        return this.numericRule(spec, op, value);
      case 'date-range':
        return this.dateRule(spec, op, value);
      case 'set':
        return this.setComparison(spec, fieldTok, op, value);
    }
  }

  private booleanRule(spec: PqlFieldSpec, op: string, value: RawValue): QueryNode {
    const raw = value.text.toLowerCase();
    if (value.kind !== 'bareword' || (raw !== 'true' && raw !== 'false')) {
      this.fail(`"${spec.label}" is yes/no — use ${spec.key} = true or ${spec.key} = false`, value.token);
    }
    let bool = raw === 'true';
    if (op === '!=') bool = !bool;
    else if (op !== '=') this.fail(`Operator "${op}" is not supported for yes/no field "${spec.label}"`, value.token);
    return this.makeRule(spec, 'boolean', bool, bool ? 'Yes' : 'No');
  }

  private textRule(spec: PqlFieldSpec, op: string, value: RawValue): QueryNode {
    const text = this.literalText(value);
    switch (op) {
      case '~':
        return this.makeRule(spec, 'text', text, text);
      case '!~':
        return { op: 'not', child: this.makeRule(spec, 'text', text, text) };
      case '=':
        return this.makeRule(spec, 'eq', text, text);
      case '!=':
        return { op: 'not', child: this.makeRule(spec, 'eq', text, text) };
      default:
        this.fail(`Operator "${op}" is not supported for text field "${spec.label}" (use ~, !~, =, !=)`, value.token);
    }
  }

  private numericRule(spec: PqlFieldSpec, op: string, value: RawValue): QueryNode {
    if (value.kind === 'numrange') {
      const a = Number(value.text);
      const b = Number(value.text2);
      const rule = this.makeRule(spec, 'range', [a, b], `${a}..${b}`);
      if (op === '=') return rule;
      if (op === '!=') return { op: 'not', child: rule };
      this.fail(`Range literals only work with = and != (e.g. ${spec.key} = ${a}..${b})`, value.token);
    }
    if (value.kind !== 'number') {
      this.fail(`"${spec.label}" is numeric — expected a number, found "${value.token.text}"`, value.token);
    }
    const n = Number(value.text);
    switch (op) {
      case '=':
        return this.makeRule(spec, 'range', [n, n], String(n));
      case '!=':
        return { op: 'not', child: this.makeRule(spec, 'range', [n, n], String(n)) };
      case '>=':
        return this.makeRule(spec, 'range', [n, Infinity], `≥ ${n}`);
      case '<=':
        return this.makeRule(spec, 'range', [-Infinity, n], `≤ ${n}`);
      case '>':
        return this.makeRule(spec, 'gt', n, `> ${n}`);
      case '<':
        return this.makeRule(spec, 'lt', n, `< ${n}`);
      default:
        this.fail(`Operator "${op}" is not supported for numeric field "${spec.label}"`, value.token);
    }
  }

  private dateRule(spec: PqlFieldSpec, op: string, value: RawValue): QueryNode {
    if (value.kind === 'daterange') {
      const rule = this.makeRule(
        spec,
        'date-range',
        [value.text, value.text2!],
        `from ${value.text} to ${value.text2}`,
      );
      if (op === '=') return rule;
      if (op === '!=') return { op: 'not', child: rule };
      this.fail(`Range literals only work with = and != (e.g. ${spec.key} = ${value.text}..${value.text2})`, value.token);
    }
    const iso = this.resolveDate(value);
    switch (op) {
      case '>=':
        return this.makeRule(spec, 'date-range', [iso, ''], `from ${iso}`);
      case '<=':
        return this.makeRule(spec, 'date-range', ['', iso], `to ${iso}`);
      case '=':
        return this.makeRule(spec, 'date-range', [iso, iso], iso);
      case '!=':
        return { op: 'not', child: this.makeRule(spec, 'date-range', [iso, iso], iso) };
      case '>':
        return this.makeRule(spec, 'date-after', iso, `after ${iso}`);
      case '<':
        return this.makeRule(spec, 'date-before', iso, `before ${iso}`);
      default:
        this.fail(`Operator "${op}" is not supported for date field "${spec.label}"`, value.token);
    }
  }

  private setComparison(spec: PqlFieldSpec, fieldTok: Token, op: string, value: RawValue): QueryNode {
    if (op === '=' ) return this.setRuleFromValues(spec, fieldTok, [value]);
    if (op === '!=') return { op: 'not', child: this.setRuleFromValues(spec, fieldTok, [value]) };
    if (op === '~') {
      // Substring match over a set-typed field (e.g. tags ~ "churn").
      const text = this.literalText(value);
      return this.makeRule(spec, 'text', text, text);
    }
    if (op === '!~') {
      const text = this.literalText(value);
      return { op: 'not', child: this.makeRule(spec, 'text', text, text) };
    }
    // Numeric comparison over a set field whose option values are all numeric
    // (e.g. altitude): desugar to IN of the option values that satisfy it.
    if (['>', '>=', '<', '<='].includes(op)) {
      const options: FilterFieldOption[] = spec.options ?? [];
      const numeric = options.length > 0 && options.every((o: FilterFieldOption) => !Number.isNaN(Number(o.value)));
      if (value.kind !== 'number' || !numeric) {
        this.fail(`Operator "${op}" is not supported for "${spec.label}" (use =, !=, IN)`, value.token);
      }
      const n = Number(value.text);
      const matching = options.filter((o: FilterFieldOption) => {
        const v = Number(o.value);
        return op === '>' ? v > n : op === '>=' ? v >= n : op === '<' ? v < n : v <= n;
      });
      if (matching.length === 0) {
        this.fail(`No "${spec.label}" values are ${op} ${n}`, value.token);
      }
      return this.makeRule(
        spec,
        'in',
        matching.map((o: FilterFieldOption) => o.value),
        matching.map((o: FilterFieldOption) => o.label).join(', '),
      );
    }
    this.fail(`Operator "${op}" is not supported for "${spec.label}"`, value.token);
  }

  private setRuleFromValues(spec: PqlFieldSpec, _fieldTok: Token, values: RawValue[]): FilterRule {
    const resolved = values.map((v) => this.resolveSetValue(spec, v));
    return this.makeRule(
      spec,
      'in',
      resolved.map((r) => r.value),
      resolved.map((r) => r.label).join(', '),
    );
  }

  private resolveSetValue(spec: PqlFieldSpec, value: RawValue): { value: string; label: string } {
    const raw = this.literalText(value);
    const options: FilterFieldOption[] = spec.options ?? [];
    if (options.length === 0) {
      // Dynamic options not loaded / free-form custom field: accept as-is.
      return { value: raw, label: raw };
    }
    const lower = raw.toLowerCase();
    const match = options.find(
      (o: FilterFieldOption) => o.value.toLowerCase() === lower || o.label.toLowerCase() === lower,
    );
    if (match) return { value: match.value, label: match.label };
    const preview = options.slice(0, 8).map((o: FilterFieldOption) => o.value).join(', ');
    this.fail(
      `"${raw}" is not a valid value for "${spec.label}" — valid values: ${preview}${options.length > 8 ? ', …' : ''}`,
      value.token,
      preview,
    );
  }

  /** Text of a literal value; resolves currentUser() and rejects other functions. */
  private literalText(value: RawValue): string {
    if (value.kind === 'function') {
      const fn = value.text.toLowerCase();
      if (fn === 'currentuser') {
        const id = this.catalog.context.currentUserId;
        if (!id) this.fail('currentUser() is not available here', value.token);
        return id;
      }
      this.fail(
        `Unknown function "${value.text}()"${fn === 'now' ? ' — now() is only valid in date comparisons' : ''}`,
        value.token,
      );
    }
    return value.text;
  }

  /** Resolve a date-position value (date literal, quoted date, now(), or -30d duration) to YYYY-MM-DD. */
  private resolveDate(value: RawValue): string {
    const now = this.catalog.context.now?.() ?? new Date();
    if (value.kind === 'date') return value.text;
    if (value.kind === 'string') {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value.text)) {
        this.fail(`Expected a date (YYYY-MM-DD), found "${value.text}"`, value.token);
      }
      return value.text;
    }
    if (value.kind === 'function') {
      if (value.text.toLowerCase() === 'now') return toIsoDate(now);
      this.fail(`Unknown function "${value.text}()" — dates accept now() or a -30d style duration`, value.token);
    }
    if (value.kind === 'duration') {
      const m = /^-(\d+(?:\.\d+)?)([dwMy])$/.exec(value.text)!;
      const amount = Number(m[1]);
      const d = new Date(now.getTime());
      if (m[2] === 'd') d.setDate(d.getDate() - amount);
      else if (m[2] === 'w') d.setDate(d.getDate() - amount * 7);
      else if (m[2] === 'M') d.setMonth(d.getMonth() - amount);
      else d.setFullYear(d.getFullYear() - amount);
      return toIsoDate(d);
    }
    this.fail(`Expected a date (YYYY-MM-DD, now(), or -30d), found "${value.token.text}"`, value.token);
  }
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
